import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  BookOpen,
  Music4,
  Plug,
  PlugZap,
  Spline,
  Volume2,
  VolumeX,
} from "lucide-react";
import {
  GLOSSARY,
  PRESETS,
  audioSupported,
  midiName,
  playNote,
  setMuted,
} from "../lib/synthLab";
import LabShell from "../components/synthlab/LabShell";
import Scope from "../components/synthlab/Scope";
import Keyboard from "../components/synthlab/Keyboard";
import SynthRack from "../components/synthlab/SynthRack";
import EnvelopeGraph from "../components/EnvelopeGraph";
import {
  useSynthVoices,
  useMidiInput,
  loadCcMap,
  MIDI_SUPPORTED,
} from "../components/synthlab/useSynthInput";
import { ccToParamValue } from "../lib/synthLab";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";

const CAN_PLAY = audioSupported();

// The instrument room: presets, the full rack, the visualizers and the
// playable keyboard — everything the focused practice screens deliberately
// leave out. Free play lives here.
const SynthLabPlayground = () => {
  const [patch, setPatch] = useState(() => ({ ...PRESETS[0].patch }));
  const [muted, setMutedState] = useState(false);
  const [showGlossary, setShowGlossary] = useState(false);
  const [midiEnabled, setMidiEnabled] = useState(false);

  const { press, release, active } = useSynthVoices(patch);

  // Bound hardware knobs drive the rack, same as in the practice rounds.
  const ccMapRef = useRef(loadCcMap());
  const onControl = useCallback((controller, value) => {
    const entry = Object.entries(ccMapRef.current).find(([, cc]) => cc === controller);
    if (!entry) return;
    setPatch((p) => ({ ...p, [entry[0]]: ccToParamValue(entry[0], value) }));
  }, []);
  const midi = useMidiInput(press, release, midiEnabled, onControl);

  useEffect(() => {
    setMuted(muted);
  }, [muted]);

  const setParam = useCallback((id, value) => {
    setPatch((p) => ({ ...p, [id]: value }));
  }, []);

  const loadPreset = useCallback((preset) => {
    setPatch({ ...preset.patch });
    playNote({ ...preset.patch }, 60, 700);
  }, []);

  return (
    <LabShell
      eyebrow="the instrument room"
      title="Playground"
      blurb="Load a character, poke at every knob, and watch the scope breathe. No grades in here."
      actions={
        <>
          <Button
            variant={showGlossary ? "default" : "outline"}
            size="sm"
            onClick={() => setShowGlossary((s) => !s)}
          >
            <BookOpen className="h-4 w-4" />
            Terms
          </Button>
          {CAN_PLAY && (
            <Button variant="outline" size="sm" onClick={() => setMutedState((m) => !m)}>
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              {muted ? "Muted" : "Sound"}
            </Button>
          )}
          <Button asChild variant="ghost" size="sm">
            <Link to="/synth-lab">
              <ArrowLeft className="h-4 w-4" />
              Lab
            </Link>
          </Button>
        </>
      }
    >
      {!CAN_PLAY && (
        <p className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Your browser doesn't support Web Audio, so playback is unavailable —
          the controls still illustrate the concepts.
        </p>
      )}

      {showGlossary && (
        <Card className="mt-5 bg-card/80 backdrop-blur">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">The vocabulary</CardTitle>
            <CardDescription>The words synth folks use for each control.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {GLOSSARY.map((g) => (
              <div key={g.term}>
                <div className="text-sm font-semibold">{g.term}</div>
                <div className="text-sm text-muted-foreground">{g.blurb}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="mt-5 bg-card/80 backdrop-blur">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Presets to explore</CardTitle>
          <CardDescription>
            Load a character, then poke at the knobs to see what makes it tick.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => loadPreset(p)}
              title={p.blurb}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:border-primary/60 hover:bg-accent"
            >
              <span>{p.emoji}</span>
              {p.name}
            </button>
          ))}
        </CardContent>
      </Card>

      {CAN_PLAY && (
        <Card className="mt-5 bg-card/80 backdrop-blur">
          <CardHeader className="flex-row items-center gap-2 pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-4 w-4 text-primary" />
              Scope
            </CardTitle>
            <CardDescription className="!mt-0">
              the live waveform — play a note and watch it change as you tweak
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-lg border border-border bg-background/60 px-2">
              <Scope />
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="mt-5 bg-card/80 backdrop-blur">
        <CardHeader className="flex-row items-center gap-2 pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Spline className="h-4 w-4 text-primary" />
            Envelope (ADSR)
          </CardTitle>
          <CardDescription className="!mt-0">
            drag the dots to shape it — or use the knobs below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-border bg-background/60 px-3 py-3">
            <EnvelopeGraph patch={patch} labeled height={120} onChange={setParam} />
          </div>
        </CardContent>
      </Card>

      <div className="mt-5">
        <SynthRack patch={patch} onParam={setParam} />
      </div>

      {CAN_PLAY && (
        <Card className="mt-5 bg-card/80 backdrop-blur">
          <CardHeader className="flex-row items-center justify-between gap-2 pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Music4 className="h-4 w-4 text-primary" />
              Play it
            </CardTitle>
            <Button
              variant={midiEnabled ? "default" : "outline"}
              size="sm"
              onClick={() => setMidiEnabled((m) => !m)}
            >
              {midiEnabled ? <PlugZap className="h-4 w-4" /> : <Plug className="h-4 w-4" />}
              {midiEnabled ? "MIDI on" : "Connect MIDI"}
            </Button>
          </CardHeader>
          <CardContent>
            {midiEnabled && (
              <div className="mb-3 rounded-lg border border-border bg-card/60 px-3 py-2 text-sm">
                {!MIDI_SUPPORTED && (
                  <span className="text-amber-700">
                    This browser doesn't support Web MIDI — try Chrome, Edge, or Firefox (Safari doesn't have it).
                  </span>
                )}
                {midi.status === "denied" && (
                  <span className="text-rose-600">
                    MIDI access was blocked. Allow it and toggle Connect MIDI again.
                  </span>
                )}
                {midi.status === "requesting" && (
                  <span className="text-muted-foreground">Requesting MIDI access…</span>
                )}
                {midi.status === "ready" && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {midi.inputs.length ? (
                      <span className="inline-flex items-center gap-1.5 font-medium text-emerald-600">
                        <PlugZap className="h-4 w-4" />
                        {midi.inputs.join(", ")}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">
                        Listening — plug in a MIDI keyboard over USB and play.
                      </span>
                    )}
                    {midi.lastNote != null && (
                      <span className="font-mono text-xs text-muted-foreground">
                        last: {midiName(midi.lastNote)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            <Keyboard press={press} release={release} active={active} />
          </CardContent>
        </Card>
      )}
    </LabShell>
  );
};

export default SynthLabPlayground;
